import React, { useState, useImperativeHandle } from 'react'
import { useFormik, FormikProvider, FieldArray } from 'formik'
import moment from 'moment';

// mui
import { Typography, Grid } from '@mui/material';

// components
import FormSectionHeader from '../../../../../components/forms/FormSectionHeader/FormSectionHeader.component';
import InputField from '../../../../../components/forms/InputField/InputField.component';
import AsyncSelectField from '../../../../../components/forms/AsyncSelectField/AsyncSelectField.component';
import ActionButton from '../../../../../components/buttons/ActionButton/ActionButton.component';
import ShippingEquipmentInput from '../../../../../components/forms/lists/ShippingEquipmentInput/ShippingEquipmentInput.component';

// queries
import { GET_ALL_JOBS_FOR_SELECT_FIELD } from '../../../../../graphql/job/job.queries';
import { GET_ALL_TRUCKS_FOR_SELECT_FIELD } from '../../../../../graphql/truck/truck.queries';
import { GET_ALL_EQUIPMENT_FOR_SELECT_FIELD_WITHOUT_QUANTITY } from '../../../../../graphql/equipment/equipment.queries';

// utils
import { calculateTotalWeight } from './ShippingLog.form.utils';

const ShippingForm = React.forwardRef(({ errors }, ref) => {
    const [ selectedEquipment, setSelectedEquipment ] = useState(null)
    const [ selectedJob, setSelectedJob ] = useState(null);
    const [ selectedTruck, setSelectedTruck ] = useState(null);

    useImperativeHandle(ref, () => ({
        getData: () => {
            return {
                ...formik.values,
                equipment: formik.values.equipment.map(item => ({
                    ...item,
                    quantity: parseInt(item.quantity, 10)
                }))
            }
        }
    }))
    
    const formik = useFormik({
        initialValues: {
            toJob: '',
            date: moment(),
            time: moment(),
            truck: '',
            equipment: [],
            equipmentSearch: ''
        },
        onSubmit: values => {
            alert(JSON.stringify(values, null, 2));
        },
    });

    const totalWeight = calculateTotalWeight(formik.values.equipment)

    return (
        <FormikProvider value={formik}>
            <form onSubmit={formik.handleSubmit}>
                <AsyncSelectField 
                    id="toJob"
                    value={formik.values.toJob}
                    onChange={value => {
                        // convert job ID to integer
                        const jobId = parseInt(value.value, 10)
                        formik.setFieldValue('toJob', jobId)
                        setSelectedJob(value.data)
                    }}
                    placeholder="Select a Job"
                    query={{
                        query: GET_ALL_JOBS_FOR_SELECT_FIELD,
                        rules: {
                            loopField: 'allJobs',
                            label: ['name'],
                            value: 'id'
                        }
                    }}
                    isValid={errors['toJob']}
                />

                <InputField 
                    id="date"
                    placeholder="Date"
                    type="date"
                    onChange={value => formik.setFieldValue('date', value)}
                    value={formik.values.date}
                    isValid={errors['date']}
                />

                <InputField 
                    id="time"
                    placeholder="Time"
                    type="time"
                    onChange={value => formik.setFieldValue('time', value)}
                    value={formik.values.time}
                    isValid={errors['time']}
                />

                <AsyncSelectField 
                    id="truck"
                    value={formik.values.truck}
                    onChange={value => {
                        setSelectedTruck(value.data)
                        formik.setFieldValue('truck', parseInt(value.value, 10)) // <-- convert to integer
                    }}
                    placeholder="Select a Truck"
                    query={{
                        query: GET_ALL_TRUCKS_FOR_SELECT_FIELD,
                        rules: {
                            loopField: 'allTrucks',
                            label: ['number'],
                            value: 'id'
                        }
                    }}
                    isValid={errors['truck']}
                />

                <FieldArray
                    name='equipment'
                    render={({ push, remove }) => (
                        <div>
                            <FormSectionHeader text='Equipment' />
                            <div className='select-and-add'>
                                <div className='select-and-add-left'>
                                    <AsyncSelectField 
                                        id="equipmentSearch"
                                        value={formik.values.equipmentSearch}
                                        placeholder="Search"
                                        onChange={value => {
                                            setSelectedEquipment(value.data)
                                            // convert equipment ID to integer
                                            formik.setFieldValue('equipmentSearch', parseInt(value.value, 10))
                                        }}
                                        query={{
                                            query: GET_ALL_EQUIPMENT_FOR_SELECT_FIELD_WITHOUT_QUANTITY,
                                            rules: {
                                                loopField: 'allEquipment',
                                                label: ['sku', 'name'],
                                                value: 'id',
                                                groupBy: 'category.name'
                                            }
                                        }}
                                        grouped
                                        showRequired={false}
                                    />
                                </div>
                                <div className='select-and-add-right'>
                                    <ActionButton 
                                        label='Add' 
                                        disabled={selectedEquipment == null} 
                                        onClick={(e) => {
                                            e.preventDefault()
                                            push({
                                                ...selectedEquipment, 
                                                id: parseInt(selectedEquipment.id, 10),
                                                quantity: '', 
                                                currentQty: selectedEquipment.quantity
                                            })
                                        }} 
                                    />
                                </div>
                            </div>

                            <Grid container>
                                <Grid item xs={6}><Typography>Weight:</Typography></Grid>
                                <Grid item xs={6} textAlign='end'>
                                    <Typography>{totalWeight} / {selectedTruck ? selectedTruck.maxLoadInLbs : "0"} lbs</Typography>
                                </Grid>
                            </Grid>
                            
                            {
                                formik.values.equipment.map((item, index) => (
                                    <ShippingEquipmentInput 
                                        key={index}
                                        prefix={`equipment.${index}`}
                                        handleChange={formik.handleChange}
                                        equipmentName={item.name}
                                        currentQty={item.currentQty}
                                        shippedQty={item.quantity}
                                        handleRemove={(e) => {
                                            e.preventDefault()
                                            remove(index)
                                        }}
                                        errors={errors['equipment'].length > index ? errors['equipment'][index] : {}}
                                    />
                                ))
                            }
                        </div>
                    )}
                />
            </form>
        </FormikProvider>
    )
})

export default ShippingForm
